import React, { useEffect, useRef, useState } from "react";
import { Building2, Shield, Lock, ArrowRight, Loader2 } from "lucide-react";
import { useGetAuthorizationUrl } from "@/hooks/auth/useSalesforceAuthGraphQL";
import type { SalesforceEnvironment } from "@/services/salesforce";
import toast from "react-hot-toast";
import { env } from "@/config/env";

/** Salesforce tokens scattered across the hero panel. Hidden by default — only
 *  visible under the cursor spotlight via the same radial mask as the grid.
 *  Positions are spread out and skip the centre band so they don't crowd the
 *  logo/welcome text when revealed. */
const SCATTERED_TOKENS: { top: string; left: string; text: string; size: string }[] = [
  // Top band
  { top: "6%",  left: "8%",  text: "LWC",             size: "text-2xl" },
  { top: "4%",  left: "38%", text: "@AuraEnabled",    size: "text-xl"  },
  { top: "10%", left: "68%", text: "trigger",         size: "text-2xl" },
  { top: "14%", left: "20%", text: "SObject",         size: "text-xl"  },
  { top: "18%", left: "82%", text: "@isTest",         size: "text-xl"  },
  { top: "22%", left: "50%", text: "Database.query",  size: "text-lg"  },
  { top: "26%", left: "12%", text: "Apex",            size: "text-2xl" },
  { top: "28%", left: "72%", text: "List<Account>",   size: "text-lg"  },
  // Middle (sides only, leave centre clear for logo/text)
  { top: "42%", left: "4%",  text: "SOQL",            size: "text-3xl" },
  { top: "46%", left: "88%", text: "Flow",            size: "text-2xl" },
  { top: "54%", left: "6%",  text: "@future",         size: "text-xl"  },
  { top: "58%", left: "84%", text: "Schema",          size: "text-xl"  },
  // Bottom band
  { top: "68%", left: "18%", text: "Permission Set",  size: "text-lg"  },
  { top: "72%", left: "62%", text: "Lightning",       size: "text-2xl" },
  { top: "78%", left: "10%", text: "VisualForce",     size: "text-lg"  },
  { top: "80%", left: "44%", text: "=>",              size: "text-3xl" },
  { top: "84%", left: "76%", text: "Batchable",       size: "text-xl"  },
  { top: "88%", left: "26%", text: "Custom Object",   size: "text-lg"  },
  { top: "92%", left: "58%", text: "REST",            size: "text-2xl" },
  { top: "94%", left: "86%", text: "Queueable",       size: "text-xl"  },
];

/** Cursor-reactive grid overlay — draws faint vertical + horizontal lines that
 *  brighten under a soft spotlight following the mouse. Pure CSS via radial
 *  gradient masks; no canvas, no rAF loop, just one mousemove → CSS variable.
 *  Same spotlight also reveals scattered Salesforce tokens. */
const CursorGrid: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
      el.style.setProperty("--my", `${e.clientY - rect.top}px`);
    };
    const onLeave = () => {
      el.style.setProperty("--mx", `-9999px`);
      el.style.setProperty("--my", `-9999px`);
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div
      ref={ref}
      className="absolute inset-0 pointer-events-auto"
      aria-hidden
      style={{
        // CSS vars default off-screen so the grid is invisible until cursor enters
        ["--mx" as never]: "-9999px",
        ["--my" as never]: "-9999px",
        // Faint base grid (always visible at very low opacity)
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
      }}
    >
      {/* Bright grid clipped by a radial mask centred on the cursor */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(96,165,250,0.55) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.55) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          WebkitMaskImage:
            "radial-gradient(220px circle at var(--mx) var(--my), rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 35%, rgba(0,0,0,0) 70%)",
          maskImage:
            "radial-gradient(220px circle at var(--mx) var(--my), rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 35%, rgba(0,0,0,0) 70%)",
        }}
      />
      {/* Salesforce tokens — revealed by the same radial mask as the bright grid */}
      <div
        className="absolute inset-0"
        style={{
          WebkitMaskImage:
            "radial-gradient(220px circle at var(--mx) var(--my), rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 35%, rgba(0,0,0,0) 70%)",
          maskImage:
            "radial-gradient(220px circle at var(--mx) var(--my), rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 35%, rgba(0,0,0,0) 70%)",
        }}
      >
        {SCATTERED_TOKENS.map((t, i) => (
          <span
            key={i}
            className={`absolute font-mono font-semibold whitespace-nowrap select-none text-blue-200/90 drop-shadow-[0_0_8px_rgba(59,130,246,0.6)] ${t.size}`}
            style={{ top: t.top, left: t.left, transform: "translate(-50%, -50%)" }}
          >
            {t.text}
          </span>
        ))}
      </div>

      {/* Soft spotlight halo */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(260px circle at var(--mx) var(--my), rgba(59,130,246,0.18), rgba(59,130,246,0) 70%)",
        }}
      />
    </div>
  );
};

/** Cycling typewriter tagline — pure React + CSS, ~30 lines.
 *  Types one line, holds, deletes, moves to the next. Loops forever. */
const TAGLINES = [
  "Stop debugging in production.",
  "Refactor 50 classes in 5 minutes.",
  "Touch one class. See what breaks.",
  "AI that knows your org.",
  "Goodbye, 'works on my org'.",
  "From SOQL to ship in one prompt.",
];

const TypewriterTagline: React.FC<{ className?: string }> = ({ className = "" }) => {
  const [lineIdx, setLineIdx] = useState(0);
  const [shown, setShown] = useState("");
  const [phase, setPhase] = useState<"typing" | "holding" | "deleting">("typing");

  useEffect(() => {
    const target = TAGLINES[lineIdx];
    let timeout: ReturnType<typeof setTimeout>;
    if (phase === "typing") {
      if (shown.length < target.length) {
        timeout = setTimeout(() => setShown(target.slice(0, shown.length + 1)), 45);
      } else {
        timeout = setTimeout(() => setPhase("holding"), 1800);
      }
    } else if (phase === "holding") {
      timeout = setTimeout(() => setPhase("deleting"), 0);
    } else if (shown.length > 0) {
      timeout = setTimeout(() => setShown(target.slice(0, shown.length - 1)), 22);
    } else {
      setLineIdx((i) => (i + 1) % TAGLINES.length);
      setPhase("typing");
    }
    return () => clearTimeout(timeout);
  }, [shown, phase, lineIdx]);

  return (
    <p className={`font-mono text-sm md:text-base ${className}`}>
      <span>{shown}</span>
      <span className="inline-block w-[2px] h-[1em] bg-current ml-0.5 align-middle animate-pulse" />
    </p>
  );
};

const envOptions: {
  id: "production" | "sandbox" | "developer_org" | "test_sandbox";
  label: string;
  icon: typeof Building2;
}[] = [
  { id: "production", label: "Production", icon: Building2 },
  { id: "sandbox", label: "Sandbox", icon: Shield },
  { id: "developer_org", label: "Developer Org", icon: Building2 },
  ...(env.showTestSandbox
    ? [{ id: "test_sandbox" as const, label: "Test Sandbox", icon: Shield }]
    : []),
];

export const Login: React.FC = () => {
  const [selectedEnv, setSelectedEnv] = useState<
    "production" | "sandbox" | "developer_org" | "test_sandbox" | null
  >(null);

  const getAuthUrlMutation = useGetAuthorizationUrl();

  const handleConnect = () => {
    if (!selectedEnv) {
      toast.error("Please select an environment");
      return;
    }

    const apiEnvironment: SalesforceEnvironment =
      selectedEnv === "developer_org"
        ? "production"
        : (selectedEnv as SalesforceEnvironment);

    getAuthUrlMutation.mutate(apiEnvironment, {
      onSuccess: (data) => {
        const url = new URL(data.authorizationUrl.url);
        url.searchParams.append("prompt", "login");
        window.location.href = url.toString();
      },
    });
  };

  return (
    <div className="min-h-[100dvh] flex flex-col md:flex-row md:min-h-0 md:h-full">
      {/* ─────────────────────────────────────────────────────────────────
          LEFT — Salesforce-vibe hero panel
          Sky-blue gradient background, animated drifting clouds, big logo
          with glow, cycling typewriter tagline. Familiar to any SF dev.
          ───────────────────────────────────────────────────────────────── */}
      <div className="hidden md:flex md:flex-1 relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950">
        {/* Logo bob — only animation left now that the floating tokens are gone */}
        <style>{`
          @keyframes float-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
          .float-bob { animation: float-bob 6s ease-in-out infinite; }
        `}</style>

        {/* Cursor-reactive grid background */}
        <CursorGrid />

        {/* Soft ambient glows — top-right blue + bottom-left indigo for depth */}
        <div className="absolute top-10 right-10 w-72 h-72 rounded-full bg-blue-500/15 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute bottom-10 left-10 w-64 h-64 rounded-full bg-indigo-500/12 blur-3xl pointer-events-none" aria-hidden />

        {/* Centered content stack — pointer-events-none on wrapper so the grid keeps receiving mousemove,
            then re-enabled on the inner card so links/text remain selectable. */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full px-8 lg:px-16 pointer-events-none">
          <div className="float-bob mb-8">
            <img
              src="/ncoder-ai-logo.png"
              alt="ncoder.ai"
              className="h-24 lg:h-32 xl:h-40 w-auto object-contain drop-shadow-[0_8px_32px_rgba(30,64,175,0.4)]"
            />
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-white text-center mb-3 drop-shadow-md">
            The <span className="text-yellow-200">Intelligence</span> to modernize.
          </h1>
          <p className="text-white/90 text-base lg:text-lg text-center mb-8 drop-shadow-sm">
            The <span className="font-semibold">Power</span> to innovate.
          </p>
          <TypewriterTagline className="text-white/95 drop-shadow-sm" />
        </div>
      </div>

      {/* Right: Form — scrollable on small screens */}
      <div className="flex-1 flex flex-col min-h-0 w-full bg-white md:border-l border-slate-200/60 md:shadow-sm">
        <div className="flex-1 flex flex-col justify-center md:justify-center min-h-0 overflow-auto">
          <div
            className="w-full max-w-md mx-auto px-4 py-6 sm:px-6 sm:py-8 md:px-8 md:py-10"
            style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
          >
            {/* Mobile / tablet compact: single column header */}
            <div className="md:hidden text-center mb-6 sm:mb-8">
              <img
                src="/ncoder-ai-logo.png"
                alt="ncoder.ai"
                className="h-12 sm:h-14 object-contain mx-auto mb-3"
              />
              <h1 className="text-xl sm:text-2xl font-semibold text-slate-800">Welcome back</h1>
              <p className="text-sm text-slate-600 mt-1">
                Choose your Salesforce environment
              </p>
            </div>

            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
              Choose environment
            </p>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {envOptions.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setSelectedEnv(id)}
                  disabled={getAuthUrlMutation.isPending}
                  className={`flex items-center gap-3 rounded-xl text-left p-4 min-h-[48px] sm:min-h-0 sm:p-4 border-2 transition-all duration-200 active:scale-[0.98]
                    ${
                      selectedEnv === id
                        ? "border-blue-500 bg-blue-50 shadow-[0_0_20px_rgba(59,130,246,0.25)]"
                        : "border-slate-200 bg-slate-50/50 hover:border-blue-300 hover:bg-blue-50/40"
                    }
                    ${getAuthUrlMutation.isPending ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-teal-500 flex-shrink-0 shadow-[0_0_12px_rgba(59,130,246,0.35)]">
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-slate-800 min-w-0 break-words">
                    {label}
                  </span>
                </button>
              ))}
            </div>

            <button
              onClick={handleConnect}
              disabled={getAuthUrlMutation.isPending || !selectedEnv}
              aria-busy={getAuthUrlMutation.isPending}
              className="mt-6 w-full min-h-[48px] sm:min-h-0 sm:h-11 rounded-xl bg-gradient-to-r from-blue-600 via-blue-500 to-teal-500 text-white font-semibold text-sm shadow-[0_8px_32px_rgba(59,130,246,0.45)]
                hover:from-blue-500 hover:via-blue-400 hover:to-teal-400 hover:shadow-[0_12px_40px_rgba(59,130,246,0.6)]
                disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none
                inline-flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98]"
            >
              {getAuthUrlMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <span>Connect to Salesforce</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            <div className="mt-6 flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50/50 border border-slate-200/60">
              <div className="p-1.5 rounded-lg bg-blue-500/10 flex-shrink-0">
                <Lock className="h-3.5 w-3.5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Enterprise Security</p>
                <p className="text-[11px] text-slate-600 leading-relaxed mt-1">
                  Your credentials are never stored. We use Salesforce OAuth 2.0.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
